import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  FrameRef,
  HOUR_MS,
  frameKey,
  generateTimeList,
  listFrames,
  orderForPrefetch,
  resolveFrame,
  selectCandidates,
  takeChunk,
} from '../functions/forecastFrames';
import { FrameUrlStore, frameUrlStore } from '../functions/frameUrlStore';

// unavailable: その時刻に対応する Asset・バンドが無い（一覧に無い・範囲外）.
export type FrameStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export type SelectedFrame = {
  key: string | null;
  validTimeMs: number;
  status: FrameStatus;
  url: string | null;
  // 最新の起算が取れず古い起算で代わりに表示しているとき、その起算時刻. それ以外は null.
  fallbackOriginMs: number | null;
};

type Args = {
  imageIdList: string[];
  increment: number;
  delta: number;
  // スライダーの初期値が確定するまでは null. 仮の値で取得を始めないため.
  selectedValidTimeMs: number | null;
  // スライダーで選べる時刻の範囲. この範囲の時刻だけを先読みする.
  rangeMs: { min: number; max: number } | null;
  store?: FrameUrlStore;
};

// 先読み1回あたりのバンド数. 本番の gunicorn は同期ワーカーで /map/ を1本ずつ処理するため,
// 大きくまとめると、その間に選んだ時刻の要求が待たされる.
const PREFETCH_CHUNK_SIZE = 3;
// 選択中の画像の読み込み完了が届かなくても（浸水表示 OFF・キャッシュ済みなど）、この時間で先読みを始める.
const PREFETCH_FALLBACK_DELAY_MS = 3000;
// 選択中の時刻を自動で取り直す回数の上限. それ以降は再試行ボタンで取り直す.
const MAX_AUTO_ATTEMPTS = 2;

const useForecastFrames = ({
  imageIdList,
  increment,
  delta,
  selectedValidTimeMs,
  rangeMs,
  store = frameUrlStore,
}: Args) => {
  // store の更新で再描画する. 状態は描画のたびに store から読む.
  useSyncExternalStore(store.subscribe, store.getVersion);

  const candidates = useMemo(() => selectCandidates(imageIdList, increment), [imageIdList, increment]);
  const bands = useMemo(() => generateTimeList(increment, delta), [increment, delta]);
  const rangeMin = rangeMs?.min ?? null;
  const rangeMax = rangeMs?.max ?? null;

  // 取得に失敗した Asset・バンドは、同じ時刻を持つ古い起算で代わりに表示・先読みする.
  // 失敗の期限が切れると未取得に戻るので、そのときに最新の起算を取り直す.
  // store の更新ごとに作り直すと先読みが毎回やり直しになるため、失敗の顔ぶれが変わったときだけ作り直す.
  const failedSignature = candidates
    .flatMap(candidate => bands.map(band => frameKey(candidate.imageId, band)))
    .filter(key => store.getEntry(key).status === 'error')
    .join(',');
  const failedKeys = useMemo(() => new Set(failedSignature ? failedSignature.split(',') : []), [failedSignature]);
  const skip = useCallback((key: string) => failedKeys.has(key), [failedKeys]);

  const frames = useMemo(
    () => (rangeMin === null || rangeMax === null ? [] : listFrames(candidates, bands, rangeMin, rangeMax, skip)),
    [candidates, bands, rangeMin, rangeMax, skip],
  );

  const resolve = useCallback(
    (validTimeMs: number) => resolveFrame(candidates, bands, validTimeMs, skip),
    [candidates, bands, skip],
  );

  const statusOf = (frame: FrameRef | null): FrameStatus =>
    frame ? store.getEntry(frame.key).status : 'unavailable';

  const selectedRef = selectedValidTimeMs === null ? null : resolve(selectedValidTimeMs);
  const selectedKey = selectedRef?.key ?? null;
  const selectedEntry = selectedRef ? store.getEntry(selectedRef.key) : null;
  const selectedStatus: FrameStatus = selectedEntry?.status ?? 'unavailable';
  const isFallback =
    !!selectedRef && selectedRef.key !== resolveFrame(candidates, bands, selectedRef.validTimeMs)?.key;
  const selectedFrame: SelectedFrame | null =
    selectedValidTimeMs === null
      ? null
      : {
          key: selectedKey,
          validTimeMs: selectedValidTimeMs,
          status: selectedStatus,
          url: selectedEntry?.status === 'ready' ? selectedEntry.url : null,
          fallbackOriginMs: selectedRef && isFallback ? selectedRef.validTimeMs - selectedRef.band * HOUR_MS : null,
        };

  // 選択中の時刻は、先読みの完了を待たずに最優先で1バンドだけ要求する.
  const selectedRefRef = useRef(selectedRef);
  selectedRefRef.current = selectedRef;
  useEffect(() => {
    const frame = selectedRefRef.current;
    if (!frame) {
      return;
    }
    const entry = store.getEntry(frame.key);
    if (entry.status === 'idle' || (entry.status === 'error' && entry.attempts < MAX_AUTO_ATTEMPTS)) {
      store.fetchBands(frame.imageId, [frame.band], { force: true });
    }
  }, [selectedKey, selectedStatus, store]);

  // 選択中の画像が読み込めたら（または一定時間たったら）残りの時刻を先読みしてよい.
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const notifySelectedTilesSettled = useCallback((key: string) => setSettledKey(key), []);
  useEffect(() => {
    if (!selectedKey || selectedStatus !== 'ready') {
      return;
    }
    const timer = setTimeout(() => setSettledKey(selectedKey), PREFETCH_FALLBACK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [selectedKey, selectedStatus]);

  const prefetchAllowed =
    selectedValidTimeMs !== null &&
    (selectedKey === null || (selectedStatus === 'ready' && settledKey === selectedKey));

  // 残りの時刻は、選択中の時刻から近い順に少しずつ1本ずつ要求する.
  // 選択が変わったら新しい要求の投入をやめ、新しい選択中の時刻に順番を譲る（実行中の要求は止めない）.
  const framesRef = useRef(frames);
  framesRef.current = frames;
  useEffect(() => {
    if (!prefetchAllowed || selectedValidTimeMs === null) {
      return;
    }
    let active = true;
    const run = async () => {
      while (active) {
        const pending = orderForPrefetch(framesRef.current, selectedValidTimeMs).filter(
          frame => store.getEntry(frame.key).status === 'idle',
        );
        const chunk = takeChunk(pending, PREFETCH_CHUNK_SIZE);
        if (!chunk) {
          return;
        }
        await store.fetchBands(chunk.imageId, chunk.bands);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [prefetchAllowed, selectedValidTimeMs, frames, store]);

  // 再生で次に進む時刻など、選択前に急いで用意したい時刻を要求し、要求後の状態を返す.
  // 先読みで失敗した時刻も、選択中と同じ回数までは取り直す（一時的な失敗で再生を止めないため）.
  const requestFrame = useCallback(
    (validTimeMs: number): FrameStatus => {
      const frame = resolve(validTimeMs);
      if (!frame) {
        return 'unavailable';
      }
      const entry = store.getEntry(frame.key);
      if (entry.status === 'idle' || (entry.status === 'error' && entry.attempts < MAX_AUTO_ATTEMPTS)) {
        store.fetchBands(frame.imageId, [frame.band], { force: true });
      }
      return store.getEntry(frame.key).status;
    },
    [resolve, store],
  );

  const retrySelected = useCallback(() => {
    const frame = selectedRefRef.current;
    if (frame) {
      store.fetchBands(frame.imageId, [frame.band], { force: true });
    }
  }, [store]);

  const readyCount = frames.filter(frame => statusOf(frame) === 'ready').length;

  return {
    selectedFrame,
    requestFrame,
    retrySelected,
    notifySelectedTilesSettled,
    prefetchProgress: { ready: readyCount, total: frames.length },
  };
};

export default useForecastFrames;
